import { RouteObject } from '@rocketlane/rocket-router';
import { lazy } from 'react';
import { PolicySubType } from 'api/ai-chat/types';
import { DocumentAgentPreviewRouteGuard } from 'components/organisms/agent/document/preview';
import { AgentBuilderRemote } from 'components/pages/agent-builder-remote';
import { SecureRoute } from '../route-with-permission-check';
import { ROUTE_PATHS } from '../routes-paths';

const ProjectGovernancePolicies = lazy(() =>
  import(
    'components/organisms/project-governance-policies/project-governance-policies'
  ).then((m) => ({ default: m.ProjectGovernancePolicies })),
);

const ProjectGovernancePoliciesContent = lazy(() =>
  import(
    'components/organisms/project-governance-policies/list-main-content'
  ).then((m) => ({ default: m.ProjectGovernancePoliciesContent })),
);

const ProjectGovernancePolicyPage = lazy(() =>
  import(
    'components/organisms/project-governance-policies/policy-create/policy-page'
  ).then((m) => ({ default: m.ProjectGovernancePolicyPage })),
);

const TimesheetPolicies = lazy(() =>
  import('components/pages/timesheet-policies/timesheet-policies').then(
    (m) => ({
      default: m.TimesheetPolicies,
    }),
  ),
);
const TimesheetPoliciesContent = lazy(() =>
  import('components/pages/timesheet-policies/list/list-main-content').then(
    (m) => ({
      default: m.TimesheetPoliciesContent,
    }),
  ),
);

const PolicyPage = lazy(() =>
  import(
    'components/pages/timesheet-policies/policy-creation/policy-page'
  ).then((m) => ({
    default: m.PolicyPage,
  })),
);

/** Feature/RBAC gates for agent-builder pages are bypassed when `AGENT_BUILDER_ACCESS_BYPASS` in `config/environment` is true. */
export const agentBuilderRoutes: RouteObject[] = [
  {
    path: ROUTE_PATHS.AGENT_BUILDER,
    children: [
      {
        index: true,
        element: (
          <SecureRoute>
            <AgentBuilderRemote />
          </SecureRoute>
        ),
      },
      {
        path: 'documentation/:documentationAgentId/preview',
        element: (
          <SecureRoute>
            <DocumentAgentPreviewRouteGuard />
          </SecureRoute>
        ),
      },

      {
        path: `${ROUTE_PATHS.TIMESHEET_GOVERNANCE}`,
        element: (
          <SecureRoute rule="TIMESHEET_POLICIES:READ">
            <TimesheetPolicies />
          </SecureRoute>
        ),
        children: [
          {
            path: `${ROUTE_PATHS.SETTINGS_TIMESHEETS_POLICIES_FLAG_FOR_REVIEW}`,
            element: (
              <TimesheetPoliciesContent
                policyType={PolicySubType.FlagForReview}
              />
            ),
          },
          {
            path: `${ROUTE_PATHS.SETTINGS_TIMESHEETS_POLICIES_BLOCK_CREATION}`,
            element: (
              <TimesheetPoliciesContent policyType={PolicySubType.AutoReject} />
            ),
          },
          {
            path: `${ROUTE_PATHS.SETTINGS_TIMESHEETS_POLICIES_BLOCK_SUBMISSION}/${ROUTE_PATHS.MATCH_ALL}`,
            element: (
              <TimesheetPoliciesContent
                policyType={PolicySubType.BlockSubmission}
              />
            ),
          },
        ],
      },
      {
        path: `${ROUTE_PATHS.TIMESHEET_GOVERNANCE}/${ROUTE_PATHS.POLICY_TYPE}/${ROUTE_PATHS.NEW}`,
        element: (
          <SecureRoute rule="TIMESHEET_POLICIES:CREATE">
            <PolicyPage />
          </SecureRoute>
        ),
      },
      {
        path: `${ROUTE_PATHS.TIMESHEET_GOVERNANCE}/${ROUTE_PATHS.POLICY_TYPE}/${ROUTE_PATHS.POLICY_ID}/${ROUTE_PATHS.EDIT}`,
        element: (
          <SecureRoute rule="TIMESHEET_POLICIES:WRITE">
            <PolicyPage />
          </SecureRoute>
        ),
      },
      {
        path: `${ROUTE_PATHS.GOVERNANCE_POLICIES}`,
        element: (
          <SecureRoute rule="PROJECT_GOVERNANCE_POLICIES:READ">
            <ProjectGovernancePolicies />
          </SecureRoute>
        ),
        children: [
          {
            path: `${ROUTE_PATHS.GOVERNANCE_POLICIES_WARN}`,
            element: (
              <ProjectGovernancePoliciesContent
                policyType={PolicySubType.WarnAndProceed}
              />
            ),
          },
          {
            path: `${ROUTE_PATHS.GOVERNANCE_POLICIES_BLOCK}`,
            element: (
              <ProjectGovernancePoliciesContent
                policyType={PolicySubType.AutoReject}
              />
            ),
          },
        ],
      },
      {
        path: `${ROUTE_PATHS.GOVERNANCE_POLICIES}/${ROUTE_PATHS.POLICY_TYPE}/${ROUTE_PATHS.NEW}`,
        element: (
          <SecureRoute rule="PROJECT_GOVERNANCE_POLICIES:CREATE">
            <ProjectGovernancePolicyPage />
          </SecureRoute>
        ),
      },
      {
        path: `${ROUTE_PATHS.GOVERNANCE_POLICIES}/${ROUTE_PATHS.POLICY_TYPE}/${ROUTE_PATHS.POLICY_ID}/${ROUTE_PATHS.EDIT}`,
        element: (
          <SecureRoute rule="PROJECT_GOVERNANCE_POLICIES:WRITE">
            <ProjectGovernancePolicyPage />
          </SecureRoute>
        ),
      },
      {
        path: ROUTE_PATHS.MATCH_ALL, // Catch all other sub-routes
        element: (
          <SecureRoute>
            <AgentBuilderRemote />
          </SecureRoute>
        ),
      },
    ],
  },
];
